import { ActionType } from 'typesafe-actions';

import * as actions from './actions';
import { PlagiarismServiceProviders } from '../../services/PlagiarismServiceProviders';
import { MAProgressionLogic } from './MAProgressionLogic';
import { GradingWorkflowAllocationUnit } from '../../features/workflowSettings/constants';

export type AssessmentAction = ActionType<typeof actions>;

export type GradeType = {
    [key: string]: number;
};

export interface Evaluator {
    id: number;
    name: string;
    email?: string;
    emailStatus?: string;
    lastLogin?: string;
    confirmationStatus?: string;
}
export type ExplanationObject = {
    explanationOfGradesDeadline: string;
    explanationOfGradesDeadlineEnabled: boolean;
    explanationOfGradesDirectSendingEnabled: boolean;
    explanationAdministrationEnabled: boolean;
    hideExplanation: boolean;
};

export interface ChildAssessment {
    childAssessmentRunId: number;
    childAssessmentRunTitle?: string;
    childAssessmentRunStartTime?: string;
    childAssessmentRunEndTime?: string;
    childAssessmentRunDuration?: number;
    maxScore?: number;
    isGraded?: boolean;
    gradingWeight?: number;
    attemptNumber?: number;
    collaborativeEvaluatorId?: number;
}

export interface CompositeInfo {
    children?: ChildAssessment[];
    parentAssessmentRunId?: number;
    parentAssessmentRunTitle?: string;
    multipleAttemptsProgressionLogic?: MAProgressionLogic;
}

export interface WorkflowPhase {
    committeeId: number;
    committeeName: string;
    name: string;
    isAllPhaseRow?: boolean;
}

export interface GradingWorkflowPhaseVisibility {
    visibleItems: string[];
    scope: string[];
}

export interface GradingWorkflowMarkingSettings {
    markingRange?: { type: string };
    visibility?: GradingWorkflowPhaseVisibility;
}

export interface GradingWorkflowDefinitionPhase {
    phaseId: string;
    name: string;
    order: number;
    markingSettings?: GradingWorkflowMarkingSettings;
    allocationSettings?: {
        allocationUnit?: GradingWorkflowAllocationUnit;
    };
}

export interface GradingWorkflowPhaseStatus {
    id: number;
    numberOfCandidates: number;
    phaseId: string;
    status: string;
}

export type AssessmentState = {
    readonly gradingReadyIssues?: any[];
    readonly assessmentRunTitle?: string;
    readonly assessmentRunEndTime?: string;
    readonly assessmentRunStartTime?: string;
    readonly assessmentRunGradingStatus?: string;
    readonly useNewGradingTool?: boolean;
    readonly collaborativeEvaluatorId?: number;
    readonly deliveringPartyId?: number;
    readonly questionSetContentItemId?: number;
    readonly questionSetRevisionId?: number;
    readonly gradeType?: string;
    readonly gradeTypeValues?: GradeType;
    readonly committees?: any[];
    readonly usePlagiarismService?: boolean;
    readonly hidePlagiarismFromGraders?: boolean;
    readonly hidePlagiarismFromGradersWhileMarking?: boolean;
    readonly isGraderViewMarkingStatusFilterEnabled?: boolean;
    readonly isAIAssistedMarkingEnabled?: boolean;
    readonly gradingPlagiarismColorDisabled?: boolean;
    readonly isAdaptive?: boolean;
    readonly explanationSettings?: ExplanationObject;
    readonly isGradingReady?: boolean;
    readonly isGradingReadyWithoutEndTime?: boolean;
    readonly externalSystem?: string;
    readonly marksMode?: string;
    readonly confirmationMode?: string;
    readonly confirmedTime?: string;
    readonly gradingLevel?: string;
    readonly evaluators?: Evaluator[];
    readonly candidateCount?: number;
    readonly numberOfEvaluatorContributors?: number;
    readonly useGradingThresholds?: boolean;
    readonly isAutomatedTestConstructionEnabled?: boolean;
    readonly gradingAgreeOnMarksEnabled?: boolean;
    readonly gradingInstructionsContentItemId?: number;
    readonly gradingInstructionsTitle?: string;
    readonly gradingInstructionsExtension?: string;
    readonly totalNumberOfDeliveriesCovered?: number;
    readonly hideJaggedStatus?: boolean;
    readonly syncedWithExternalSystem?: boolean;
    readonly autoSyncValue?: number;
    readonly lastSyncDate?: string;
    readonly totalNumberOfCandidates?: number;
    readonly totalNumberOfEvaluators?: number;
    readonly isLoadingExternalTestSync?: boolean;
    readonly uiOverrideLang?: string;
    readonly maxScore?: number;
    readonly maxExtraTime?: number;
    readonly latestDeliveryDeadline?: string;
    readonly weightThresholdDraftId?: number;
    readonly questionAssignmentDraftId?: number;
    readonly authenticationError?: boolean;
    readonly subjectCode?: string;
    readonly subjectTitle?: string;
    readonly variant?: string;
    readonly versionCode?: string;
    readonly evaluationType?: string;
    readonly gradingQuestionAssignmentEnabled?: boolean;
    readonly gradingQuestionAssignmentAccessLevel?: string;
    readonly gradingQuestionAssignmentAdminLevel?: string;
    readonly isTransferringGrades?: boolean;
    readonly gradeTransferTimestamp?: string;
    readonly hasGradesToTransfer?: boolean;
    readonly hasQuestionWeights?: boolean;
    readonly hasThresholds?: boolean;
    readonly quickCriteriaEnabled?: boolean;
    readonly hasMpLogo?: boolean;
    readonly annotationsShared?: boolean;
    readonly atLeastOneCandidateConfirmed?: boolean;
    readonly mpLogoUrl?: string;
    readonly marksReportScoreDisplayMode?: string;
    readonly collaborativeMarking?: boolean;
    readonly studentPreviewEnabled?: boolean;
    readonly compositeInfo?: CompositeInfo;
    readonly includeBlankDeliveries?: boolean;
    readonly questionWeightOverrideEnabled?: boolean;
    readonly amqpEnabled?: boolean;
    readonly numberOfNonStimulusQuestions?: number;
    readonly decimalBandsCriteriaEnabled?: boolean;
    readonly answerKeyCorrectionEnabled?: boolean;
    readonly questionAnswerKeyCorrectionDraftId?: number;
    readonly externalTest?: boolean;
    readonly videoBasedOralExam?: boolean;
    readonly hasPlagiarismQuestionType?: boolean;
    readonly enablePlannerConfirmOnBehalfOfGraders?: boolean;
    readonly isFinalGradePublishedToStudentDashboard?: boolean;
    readonly explanationAdministrationEnabled?: boolean;
    readonly isAutoDeliveryEnabled?: boolean;
    readonly autoDeliveryAllowMultipleSubmission?: boolean;
    readonly finalGrade?: string;
    readonly round?: number;
    readonly isMarksOnlyExam?: boolean;
    readonly externalLtiDeliveryStatus?: string;
    readonly candidateGradingComplete?: boolean;
    readonly rubricsEnabled?: boolean;
    readonly isAssessmentPath?: boolean;
    readonly hideQuestionTitles?: boolean;
    readonly isWebRTCOralExam?: boolean;
    readonly isUploadedFilesPdfConversion?: boolean;
    readonly isGradingBeforeEndTimeEnabled?: boolean;
    readonly gradingOnAssessmentPathParentEnabled?: boolean;
    readonly compositeGradeCalculationRule?: string;
    readonly maxNumberOfAttempts?: number;
    readonly isMultipleAttemptsParent?: boolean;
    readonly isMultipleAttemptsChild?: boolean;
    readonly attemptNumber?: number;
    readonly enableBulkReleaseOfExplanations?: boolean;
    readonly numberOfCandidatesWithExplanationNotSent?: number;
    readonly displayAdditionalScore?: boolean;
    readonly antiPlagiarismServiceProvider?: PlagiarismServiceProviders;
    readonly questionsAttachmentInfo?: QuestionsAttachmentInfo;
    readonly isDecoupleGradingOnParentFromChildrenEnabled?: boolean;
    readonly isCEQTest?: boolean;
    readonly availableEventFlagTypes?: [];
    readonly hasPullingSections?: boolean;
    readonly hasManualSelectionSections?: boolean;
    readonly supportedUiLanguages?: string;
    readonly hasNextAttemptCreated?: boolean;
    readonly candidateSubmissionAccessLevel?: string;
    readonly isMAProgressionLogicEnabled?: boolean;
    readonly nextAttemptStartTime?: string;
    readonly nextAttemptEndTime?: string;
    readonly hasMaximaQuestions?: boolean;
    readonly gradingWorkspaceDetailViewEnabled?: boolean;
    readonly isStudentReportEnabled?: boolean;
    readonly isOriginalityReportSharingEnabled?: boolean;
    readonly isRequestOriginalityReportEnabled?: boolean;
    readonly marksReportEnabled?: boolean;
    readonly usePlagiarismExternalServicePolling?: boolean;
    readonly originalityReportSharingMethod?: string;
    readonly skipQuestionSubmissionReviewEnabled?: boolean;
    readonly workflowPhases?: WorkflowPhase[];
    readonly isGradingWorkflowsEnabledForTest?: boolean;
    readonly gradingWorkflowRunUserId?: number;
    readonly autoMarkBlankManualResponsesEnabled?: boolean;
    readonly displayIOPercentageScore?: boolean;
    readonly gradingWorkflowDefinition?: GradingWorkflowDefinitionPhase[];
    readonly gradingWorkflowStatus?: string;
    readonly gradingWorkflowPhases?: GradingWorkflowPhaseStatus[];
};

export interface QuestionsAttachmentInfo { partyIds: string[]; questionIds: string[] }


export const initialState: AssessmentState = {
    assessmentRunTitle: '',
    useNewGradingTool: false,
    usePlagiarismService: false,
    isAdaptive: false,
    explanationSettings: null,
    totalNumberOfDeliveriesCovered: 0,
    authenticationError: false,
    includeBlankDeliveries: true,
    amqpEnabled: false,
    workflowPhases: undefined,
    candidateSubmissionAccessLevel: undefined,
    skipQuestionSubmissionReviewEnabled: false,
    isGraderViewMarkingStatusFilterEnabled: false,
    isAIAssistedMarkingEnabled: false,
    gradingWorkflowDefinition: undefined,
    gradingWorkflowStatus: undefined,
    gradingWorkflowPhases: undefined,
};

export const assessmentReducer = (
    state = initialState,
    action: AssessmentAction
) => {
    switch (action.type) {
    case actions.ContextTypeKeys.ASSESSMENT_INFO_LOADED:
        return {
            ...state,
            ...action.payload,
        };
    case actions.ContextTypeKeys.AUTHENTICATION_ERROR:
        return {
            ...state,
            authenticationError: action.payload.authError,
        };
    case actions.ContextTypeKeys.ASSESSMENT_QUESTIONS_ATTACHMENT_INFO_LOADED:
        return {
            ...state,
            questionsAttachmentInfo: action.payload,
        };
    case actions.ContextTypeKeys.GRADING_WORKFLOW_DEFINITION_LOADED:
        return {
            ...state,
            gradingWorkflowDefinition: action.payload.definition,
            gradingWorkflowStatus: action.payload.status,
            gradingWorkflowPhases: action.payload.phases,
        };
    default:
        return state;
    }
};
